import React, { useState } from 'react';
import {
  Search,
  Filter,
  TableProperties,
  Database,
  ShieldCheck,
  CheckCircle2,
  Lock,
  ArrowRight,
  BookOpen,
  Award,
  Sparkles,
  Layers,
  FileCheck2,
  ChevronRight,
  Eye,
  GitBranch,
  Info
} from 'lucide-react';
import { AppView } from '../../types';
import { dataGovernanceStore, DataAsset, DataDomain } from '../../services/data/dataGovernanceStore';

interface EnterpriseDataCatalogPortalProps {
  onNavigateToView: (view: AppView) => void;
}

export const EnterpriseDataCatalogPortal: React.FC<EnterpriseDataCatalogPortalProps> = ({
  onNavigateToView
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDomain, setSelectedDomain] = useState<string>('');
  const [selectedLifecycle, setSelectedLifecycle] = useState<string>('');
  const [selectedAsset, setSelectedAsset] = useState<DataAsset | null>(null);

  const assets = dataGovernanceStore.searchCatalog(searchTerm, selectedDomain as any, selectedLifecycle);
  const domains = dataGovernanceStore.getDomains();

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#1E293B] font-sans pb-16">
      {/* Top Navigation */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-20 shadow-2xs">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center">
              <TableProperties className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2.5 py-0.5 rounded-full border border-indigo-100 uppercase">
                  Enterprise Data Catalog
                </span>
                <span className="text-xs text-slate-500">YamooPay Data OS</span>
              </div>
              <h1 className="text-lg font-black text-slate-900">Portal & Discovery Self-Service</h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => onNavigateToView('cdo-command-center')}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-lg flex items-center gap-2 transition-colors cursor-pointer"
            >
              <Database className="w-4 h-4" />
              CDO Command Center
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Search Hero Box */}
        <div className="bg-gradient-to-br from-indigo-900 to-slate-900 text-white p-8 rounded-3xl shadow-lg relative overflow-hidden">
          <div className="max-w-2xl relative z-10 space-y-4">
            <span className="px-3 py-1 bg-indigo-500/30 text-indigo-200 border border-indigo-400/30 rounded-full text-xs font-bold uppercase tracking-wider">
              Metadata & Lineage Discovery
            </span>
            <h2 className="text-2xl font-black text-white tracking-tight">
              Explorez et découvrez tous les Data Assets certifiés de YamooPay
            </h2>
            <p className="text-sm text-indigo-100/90 leading-relaxed">
              Recherchez parmi les tables PostgreSQL, Data Products, métriques gouvernées, schémas fiscaux et datasets RAG de confiance.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <div className="relative flex-1">
                <Search className="w-5 h-5 text-slate-400 absolute left-4 top-3.5" />
                <input
                  type="text"
                  placeholder="Rechercher par nom de table, colonne, propriétaire ou description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white text-slate-900 rounded-xl text-sm focus:outline-none shadow-md font-medium"
                />
              </div>

              <select
                value={selectedDomain}
                onChange={(e) => setSelectedDomain(e.target.value)}
                className="bg-white text-slate-900 px-4 py-3 rounded-xl text-sm font-semibold focus:outline-none shadow-md"
              >
                <option value="">Tous les Data Domains</option>
                {domains.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Catalog Asset Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {assets.map((asset) => (
            <div
              key={asset.id}
              className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs hover:border-indigo-300 transition-all flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="px-2.5 py-0.5 bg-slate-100 text-slate-700 text-xs font-mono font-bold rounded">
                    {asset.type}
                  </span>
                  <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-full border border-emerald-200 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    {asset.certificationStatus}
                  </span>
                </div>

                <h3 className="text-base font-bold font-mono text-indigo-900 mb-1">{asset.name}</h3>
                <p className="text-xs text-slate-600 mb-3 min-h-[32px]">{asset.businessDefinition}</p>

                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1 text-xs text-slate-700 mb-4">
                  <div><strong>Domain:</strong> {asset.domain}</div>
                  <div><strong>System of Record:</strong> {asset.systemOfRecord}</div>
                  <div><strong>Propriétaire:</strong> {asset.owner}</div>
                </div>
              </div>

              <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                <span className="text-xs font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                  {asset.qualityTier}
                </span>
                <button
                  onClick={() => setSelectedAsset(asset)}
                  className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold flex items-center gap-1 transition-colors cursor-pointer"
                >
                  <Eye className="w-3.5 h-3.5" />
                  Consulter Metadata
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Selected Asset Drawer */}
        {selectedAsset && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-6 shadow-xl border border-slate-200">
              <div className="flex justify-between items-start border-b border-slate-200 pb-4">
                <div>
                  <span className="px-2.5 py-0.5 bg-indigo-50 text-indigo-700 text-xs font-bold rounded-full border border-indigo-200 uppercase font-mono">
                    {selectedAsset.type}
                  </span>
                  <h3 className="text-xl font-bold font-mono text-slate-900 mt-1">{selectedAsset.name}</h3>
                  <p className="text-sm text-slate-600 mt-0.5">{selectedAsset.description}</p>
                </div>
                <button
                  onClick={() => setSelectedAsset(null)}
                  className="text-slate-400 hover:text-slate-600 text-lg font-bold cursor-pointer"
                >
                  ✕
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl text-xs text-slate-700 border border-slate-200">
                <div><strong>Business Definition:</strong> {selectedAsset.businessDefinition}</div>
                <div><strong>Technical Definition:</strong> {selectedAsset.technicalDefinition}</div>
                <div><strong>Data Owner:</strong> {selectedAsset.owner}</div>
                <div><strong>Data Steward:</strong> {selectedAsset.steward}</div>
              </div>

              <div>
                <h4 className="font-bold text-slate-900 text-sm mb-3">Dictionnaire des Colonnes ({selectedAsset.fields.length})</h4>
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 font-bold text-slate-700 border-b border-slate-200">
                      <tr>
                        <th className="p-3">Champ</th>
                        <th className="p-3">Physical Type</th>
                        <th className="p-3">Semantic Type</th>
                        <th className="p-3">Sensibilité</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {selectedAsset.fields.map((f, i) => (
                        <tr key={i}>
                          <td className="p-3 font-mono font-bold text-indigo-900">{f.name}</td>
                          <td className="p-3 font-mono text-slate-600">{f.physicalType}</td>
                          <td className="p-3 font-semibold text-indigo-600">{f.semanticType}</td>
                          <td className="p-3">
                            <span className={`px-2 py-0.5 rounded font-bold ${f.sensitivity === 'HIGH' ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-700'}`}>
                              {f.sensitivity}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-between items-center pt-4 border-t border-slate-200">
                <div className="text-xs text-slate-500 font-mono">Dernière vérification: {selectedAsset.lastVerifiedAt}</div>
                <button
                  onClick={() => setSelectedAsset(null)}
                  className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-bold cursor-pointer"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
